import { Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, Building2, KeyRound, Receipt, PieChart, Wrench, ShieldCheck, FileText, CreditCard, Settings as SettingsIcon, Megaphone, Inbox, CalendarDays, HandCoins, ListChecks } from "lucide-react";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import Dashboard from "@/pages/biz/Dashboard";
import Landlords from "@/pages/biz/Landlords";
import Properties from "@/pages/biz/Properties";
import PropertyDetail from "@/pages/biz/PropertyDetail";
import Tenancies from "@/pages/biz/Tenancies";
import Rent from "@/pages/biz/Rent";
import Finances from "@/pages/biz/Finances";
import Maintenance from "@/pages/biz/Maintenance";
import Compliance from "@/pages/biz/Compliance";
import Documents from "@/pages/biz/Documents";
import Billing from "@/pages/biz/Billing";
import Settings from "@/pages/biz/Settings";
import Listings from "@/pages/biz/Listings";
import ListingEditor from "@/pages/biz/ListingEditor";
import Enquiries from "@/pages/biz/Enquiries";
import Viewings from "@/pages/biz/Viewings";
import Offers from "@/pages/biz/Offers";
import Progress from "@/pages/biz/Progress";

export default function BusinessApp() {
  const { session, logout } = useAuth();
  const b = session.business;
  const isAgent = b?.type === "agent";
  if (b?.status === "suspended") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="panel max-w-md text-center py-12" data-testid="suspended-screen">
          <h1 className="text-3xl font-extrabold">Account suspended</h1>
          <p className="text-slate-500 mt-2">This business account has been suspended. Please contact support.</p>
          <button onClick={logout} className="mt-6 px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold" data-testid="suspended-logout">Log out</button>
        </div>
      </div>
    );
  }
  const items = [
    { id: "dashboard", to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard },
    ...(isAgent ? [{ id: "landlords", to: "/app/landlords", label: "Landlords", icon: Users }] : []),
    { id: "properties", to: "/app/properties", label: "Properties", icon: Building2 },
    { id: "tenancies", to: "/app/tenancies", label: "Tenancies", icon: KeyRound },
    { id: "listings", to: "/app/listings", label: "Listings", icon: Megaphone },
    { id: "enquiries", to: "/app/enquiries", label: "Enquiries", icon: Inbox },
    { id: "viewings", to: "/app/viewings", label: "Viewings", icon: CalendarDays },
    { id: "offers", to: "/app/offers", label: "Offers", icon: HandCoins },
    { id: "progress", to: "/app/progress", label: "Progress", icon: ListChecks },
    { id: "rent", to: "/app/rent", label: "Rent", icon: Receipt },
    { id: "finances", to: "/app/finances", label: "Income & expenses", icon: PieChart },
    { id: "maintenance", to: "/app/maintenance", label: "Maintenance", icon: Wrench },
    { id: "compliance", to: "/app/compliance", label: "Compliance", icon: ShieldCheck },
    { id: "documents", to: "/app/documents", label: "Documents", icon: FileText },
    { id: "billing", to: "/app/billing", label: "Plan & billing", icon: CreditCard },
    { id: "settings", to: "/app/settings", label: "Settings", icon: SettingsIcon },
  ];
  return (
    <Shell items={items} title={b?.name} subtitle={`${isAgent ? "Letting agent" : "Landlord"} · ${session.plan?.name || "No plan"} · ${b?.currency}`}>
      <Routes>
        <Route index element={<Dashboard />} />
        {isAgent && <Route path="landlords" element={<Landlords />} />}
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="tenancies" element={<Tenancies />} />
        <Route path="listings" element={<Listings />} />
        <Route path="listings/:id" element={<ListingEditor />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="viewings" element={<Viewings />} />
        <Route path="offers" element={<Offers />} />
        <Route path="progress" element={<Progress />} />
        <Route path="rent" element={<Rent />} />
        <Route path="finances" element={<Finances />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="documents" element={<Documents />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Shell>
  );
}
